import {
  Link as LinkIcon,
  Phone,
  Share2,
  Video,
  Images,
  FileText,
  HelpCircle,
  CalendarClock,
  MapPin,
  ShoppingBag,
  type LucideIcon,
} from 'lucide-react';
import { SOCIAL_LINK_TYPES, type LinkType } from './linkTypes';

export type SectionType =
  | 'links'
  | 'contact'
  | 'social'
  | 'videos'
  | 'gallery'
  | 'files'
  | 'faq'
  | 'booking'
  | 'locations'
  | 'products';

/** How an item is rendered on the public page. */
export type RenderStyle =
  | 'link-list' // icon + label + value + chevron (default link list)
  | 'icon-row' // small clickable icon buttons in a row (social)
  | 'video-cards' // title + description + tap-to-open
  | 'photo-grid' // grid of images
  | 'file-list' // file rows with download chevron
  | 'faq-list' // expandable Q&A
  | 'booking-embed' // inline iframe (Calendly / Square / etc.)
  | 'location-cards' // map + rating + directions per business
  | 'product-grid'; // image + name + price + add-to-order

export interface SectionTypeConfig {
  type: SectionType;
  label: string;
  description: string;
  icon: LucideIcon;
  defaultName: string;
  renderStyle: RenderStyle;
  /** Which fields the editor shows when adding/editing items in this section. */
  itemFields: ('label' | 'value' | 'description')[];
  /** Restrict the link-type dropdown when adding items. Empty means free choice. */
  allowedLinkTypes?: LinkType[];
  /** A fixed link type used for every item in this section (e.g. videos always 'video-ish'). */
  fixedLinkType?: LinkType;
  /** Help text shown above items list in the editor. */
  hint?: string;
}

export const SECTION_TYPES: Record<SectionType, SectionTypeConfig> = {
  contact: {
    type: 'contact',
    label: 'Contact info',
    description: 'Phone, email, address — tappable.',
    icon: Phone,
    defaultName: 'Contact',
    renderStyle: 'link-list',
    itemFields: ['label', 'value'],
    allowedLinkTypes: ['phone', 'email', 'website', 'address', 'calendar'],
    hint: 'Each row is icon + label + value. Tap opens the right app (phone dials, email composes, address opens maps).',
  },
  social: {
    type: 'social',
    label: 'Socials',
    description: 'Icon-only buttons for social profiles.',
    icon: Share2,
    defaultName: 'Socials',
    renderStyle: 'icon-row',
    itemFields: ['value'],
    allowedLinkTypes: SOCIAL_LINK_TYPES,
    hint: 'Shows only the platform icon. Pick the platform, then paste a URL or just your @handle.',
  },
  videos: {
    type: 'videos',
    label: 'Videos',
    description: 'Embeddable links with title + caption.',
    icon: Video,
    defaultName: 'Videos',
    renderStyle: 'video-cards',
    itemFields: ['label', 'value', 'description'],
    fixedLinkType: 'youtube',
    hint: 'Paste a YouTube / Vimeo URL, give it a title, and add a short summary.',
  },
  gallery: {
    type: 'gallery',
    label: 'Photo gallery',
    description: 'A grid of images.',
    icon: Images,
    defaultName: 'Gallery',
    renderStyle: 'photo-grid',
    itemFields: ['value', 'description'],
    fixedLinkType: 'other',
    hint: 'For v1: paste image URLs. Captions appear on tap.',
  },
  files: {
    type: 'files',
    label: 'Files',
    description: 'PDFs, decks, downloads.',
    icon: FileText,
    defaultName: 'Files',
    renderStyle: 'file-list',
    itemFields: ['label', 'value', 'description'],
    fixedLinkType: 'document',
    hint: 'Each file has a name, a link, and an optional description.',
  },
  faq: {
    type: 'faq',
    label: 'FAQ',
    description: 'Question + answer.',
    icon: HelpCircle,
    defaultName: 'FAQ',
    renderStyle: 'faq-list',
    itemFields: ['label', 'description'],
    fixedLinkType: 'other',
    hint: 'Label = question. Description = answer. The value field stays empty.',
  },
  locations: {
    type: 'locations',
    label: 'Locations',
    description: 'Business locations with map, rating & directions.',
    icon: MapPin,
    defaultName: 'Locations',
    renderStyle: 'location-cards',
    itemFields: ['label', 'value', 'description'],
    fixedLinkType: 'location',
    hint: 'Search for your business or paste a Google Maps link — we pull the name, star rating, address and phone. Add as many locations as you like; each gets its own map and Directions button.',
  },
  products: {
    type: 'products',
    label: 'Products',
    description: 'Sell things — photo, price, add to order.',
    icon: ShoppingBag,
    defaultName: 'Shop',
    renderStyle: 'product-grid',
    itemFields: ['label', 'value', 'description'],
    fixedLinkType: 'product',
    hint: 'Each product has a photo, name, price and short description. Visitors add items to an order and pay with the methods you switch on under Shop.',
  },
  booking: {
    type: 'booking',
    label: 'Booking',
    description: 'Inline Calendly, Square, Cal.com, Acuity & more.',
    icon: CalendarClock,
    defaultName: 'Book a time',
    renderStyle: 'booking-embed',
    itemFields: ['label', 'value', 'description'],
    fixedLinkType: 'booking',
    hint: 'Paste your full booking URL — we detect Calendly, Cal.com, Square Appointments, Acuity, Setmore, SimplyBook, HubSpot Meetings, Microsoft Bookings, TidyCal, and YouCanBook.me. Other URLs render as a "Book now" button.',
  },
  links: {
    type: 'links',
    label: 'Links',
    description: 'A general list of links.',
    icon: LinkIcon,
    defaultName: 'Links',
    renderStyle: 'link-list',
    itemFields: ['label', 'value'],
    hint: 'A general catch-all. Use Contact, Socials, Videos, etc. for typed sections.',
  },
};

export const SECTION_TYPE_LIST: SectionTypeConfig[] = [
  SECTION_TYPES.contact,
  SECTION_TYPES.products,
  SECTION_TYPES.locations,
  SECTION_TYPES.booking,
  SECTION_TYPES.social,
  SECTION_TYPES.videos,
  SECTION_TYPES.gallery,
  SECTION_TYPES.files,
  SECTION_TYPES.faq,
  SECTION_TYPES.links,
];

export function sectionConfig(type: string | null | undefined): SectionTypeConfig {
  if (type && type in SECTION_TYPES) return SECTION_TYPES[type as SectionType];
  return SECTION_TYPES.links;
}
